import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { 
  Calendar, 
  Loader2, 
  Save, 
  ChevronRight, 
  ChevronDown,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import GovernanceFeedbackSection from './GovernanceFeedbackSection';
import { DELIVERABLE_STATUS_OPTIONS, DEFAULT_PHASE_IDS } from './constants';

const TimelineDialog = ({ pillar, open, onOpenChange, onSave, isAdmin, isLeadership, currentUser, scopePillar }) => {
  const [content, setContent] = useState(pillar?.content || {});
  const [saving, setSaving] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState({});

  useEffect(() => {
    if (pillar) setContent(pillar.content || {});
  }, [pillar]);

  // Toggle phase collapse
  const togglePhaseCollapse = (phaseId) => {
    setCollapsedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  // Get all phase IDs from Scope (default + custom)
  const getScopePhaseIds = () => {
    const customPhaseIds = scopePillar?.content?.custom_phases || [];
    return [...DEFAULT_PHASE_IDS, ...customPhaseIds];
  };

  // Get phase name from Scope's content.phase_names or generate default
  const getScopePhaseName = (phaseId) => {
    if (scopePillar?.content?.phase_names?.[phaseId]) {
      return scopePillar.content.phase_names[phaseId];
    }
    const defaultIdx = DEFAULT_PHASE_IDS.indexOf(phaseId);
    if (defaultIdx !== -1) {
      return `Phase ${defaultIdx + 1}`;
    }
    return phaseId;
  };

  // Build SCOPE_PHASES array with names from Scope (includes custom phases)
  const SCOPE_PHASES = getScopePhaseIds().map(phaseId => ({
    id: phaseId,
    name: getScopePhaseName(phaseId),
    isCustom: !DEFAULT_PHASE_IDS.includes(phaseId),
  }));

  // Get phase status from Scope (source of truth)
  const getScopePhaseStatus = (phaseId) => {
    return scopePillar?.content?.phase_statuses?.[phaseId] || 'planning';
  };

  // Get phase due date from Scope
  const getScopePhaseDueDate = (phaseId) => {
    return scopePillar?.content?.phase_due_dates?.[phaseId] || '';
  };

  // Get deliverables from Scope for a phase
  const getScopeDeliverables = (phaseId) => {
    return scopePillar?.content?.phases?.[phaseId] || [];
  };

  // Get sub-phases from Scope for a phase
  const getScopeSubPhases = (phaseId) => {
    return scopePillar?.content?.sub_phases?.[phaseId] || [];
  };

  // Calculate phase stats including sub-phases
  const getPhaseStats = (phaseId) => {
    let totalDeliverables = 0;
    let completedDeliverables = 0;
    let totalTasks = 0;
    let completedTasks = 0;

    // Phase-level deliverables
    const phaseDeliverables = getScopeDeliverables(phaseId);
    totalDeliverables += phaseDeliverables.length;
    completedDeliverables += phaseDeliverables.filter(d => d.status === 'completed').length;
    phaseDeliverables.forEach(d => {
      totalTasks += (d.tasks || []).length;
      completedTasks += (d.tasks || []).filter(t => t.completed).length;
    });

    // Sub-phase deliverables
    const subPhases = getScopeSubPhases(phaseId);
    subPhases.forEach(sp => {
      const spDeliverables = sp.deliverables || [];
      totalDeliverables += spDeliverables.length;
      completedDeliverables += spDeliverables.filter(d => d.status === 'completed').length;
      spDeliverables.forEach(d => {
        totalTasks += (d.tasks || []).length;
        completedTasks += (d.tasks || []).filter(t => t.completed).length;
      });
    });

    const percentComplete = totalTasks > 0 
      ? Math.round((completedTasks / totalTasks) * 100) 
      : (totalDeliverables > 0 ? Math.round((completedDeliverables / totalDeliverables) * 100) : 0);

    return { 
      totalDeliverables, 
      completedDeliverables, 
      totalTasks, 
      completedTasks, 
      percentComplete,
      subPhasesCount: subPhases.length
    };
  };

  // Initialize phase_data if not present (stores timeline-specific info like notes)
  const getPhaseData = (phaseId) => {
    const phaseData = content.phase_data || {};
    const data = phaseData[phaseId] || { notes: '' };
    data.status = getScopePhaseStatus(phaseId);
    data.due_date = getScopePhaseDueDate(phaseId);
    return data;
  };

  const updatePhaseData = (phaseId, field, value) => {
    const phaseData = { ...(content.phase_data || {}) };
    phaseData[phaseId] = { ...getPhaseData(phaseId), [field]: value };
    setContent({ ...content, phase_data: phaseData });
  };

  // Get projected completion date for a deliverable (stored in Timeline content)
  const getProjectedDate = (deliverableId) => {
    return content.projected_dates?.[deliverableId] || '';
  };

  // Update projected completion date for a deliverable
  const updateProjectedDate = (deliverableId, date) => {
    const projectedDates = { ...(content.projected_dates || {}) };
    projectedDates[deliverableId] = date;
    setContent({ ...content, projected_dates: projectedDates });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(pillar.id, { content });
      toast.success('Timeline updated');
      onOpenChange(false);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Calculate overall progress from all phases
  const calculateOverallProgress = () => {
    let totalTasks = 0;
    let completedTasks = 0;
    SCOPE_PHASES.forEach(phase => {
      const stats = getPhaseStats(phase.id);
      totalTasks += stats.totalTasks;
      completedTasks += stats.completedTasks;
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const overallProgress = calculateOverallProgress();
  const statusOptions = DELIVERABLE_STATUS_OPTIONS;

  // Check if a phase is late (past due date and not completed/release_ready)
  const isLate = (phaseId) => {
    const phaseData = getPhaseData(phaseId);
    if (phaseData.status === 'completed' || phaseData.status === 'release_ready') return false;
    if (!phaseData.due_date) return false;
    return new Date(phaseData.due_date) < new Date();
  };

  // Check if a deliverable is late
  const isDeliverableLate = (deliverable) => {
    if (deliverable.status === 'completed') return false;
    if (!deliverable.due_date) return false;
    return new Date(deliverable.due_date) < new Date();
  };

  // Get phase card background color - dark mode friendly
  const getPhaseColor = (phaseId) => {
    const phaseData = getPhaseData(phaseId);
    if (isLate(phaseId)) return 'bg-red-500/15 border-red-500/40';
    if (phaseData.status === 'completed') return 'bg-green-500/15 border-green-500/40';
    if (phaseData.status === 'release_ready') return 'bg-emerald-500/15 border-emerald-500/40';
    if (phaseData.status === 'needs_attention') return 'bg-red-500/15 border-red-500/40';
    if (['building', 'coding', 'internal_testing', 'team_testing', 'debug', 'edit_upgrades', 'final_testing'].includes(phaseData.status)) {
      return 'bg-blue-500/10 border-blue-500/30';
    }
    if (['planning', 'design'].includes(phaseData.status)) {
      return 'bg-slate-500/10 border-slate-500/30';
    }
    return 'bg-slate-500/10 border-slate-500/30';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" /> Timeline
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span>Project timeline synced with Scope</span>
            <span className="text-xs font-medium">Overall Progress: <span className="text-blue-600">{overallProgress}%</span></span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Project Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={content.start_date || ''} 
                onChange={(e) => setContent({...content, start_date: e.target.value})} disabled={!isAdmin} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Target Completion</Label>
              <Input type="date" value={content.target_completion || ''} 
                onChange={(e) => setContent({...content, target_completion: e.target.value})} disabled={!isAdmin} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Current Phase</Label>
              <Input value={content.current_phase || ''} 
                onChange={(e) => setContent({...content, current_phase: e.target.value})} disabled={!isAdmin} className="mt-1 h-8 text-sm" placeholder="e.g., Phase 1" />
            </div>
          </div>

          {/* Overall Progress Bar */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Project Progress</Label>
              <span className="text-sm font-bold text-blue-600">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>

          {/* Phases from Scope */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Phases & Deliverables (from Scope)</Label>
            <div className="space-y-2">
              {SCOPE_PHASES.map((phase) => {
                const phaseData = getPhaseData(phase.id);
                const stats = getPhaseStats(phase.id);
                const late = isLate(phase.id);
                const isCollapsed = collapsedPhases[phase.id] !== false;
                const phaseDeliverables = getScopeDeliverables(phase.id);
                const subPhases = getScopeSubPhases(phase.id);
                
                return (
                  <div key={phase.id} className={`rounded-lg border-2 ${getPhaseColor(phase.id)} overflow-hidden`}>
                    {/* Phase Header - Clickable */}
                    <div 
                      className="p-3 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => togglePhaseCollapse(phase.id)}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {isCollapsed ? <ChevronRight className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${late ? 'bg-red-500' : statusOptions.find(s => s.value === phaseData.status)?.color || 'bg-slate-400'}`} />
                        <span className="font-semibold text-sm flex-1">{phase.name}</span>
                        
                        {/* Status Badge */}
                        <Badge variant="outline" className={`text-[10px] ${statusOptions.find(s => s.value === phaseData.status)?.color?.replace('bg-', 'border-')}`}>
                          {statusOptions.find(s => s.value === phaseData.status)?.label || 'Planning'}
                        </Badge>
                        
                        {/* Due Date */}
                        {phaseData.due_date && (
                          <Badge variant="outline" className={`text-[10px] ${late ? 'border-red-500 text-red-500' : ''}`}>
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(phaseData.due_date).toLocaleDateString()}
                          </Badge>
                        )}
                        
                        {/* Stats */}
                        <Badge variant="secondary" className="text-[10px]">
                          {stats.totalDeliverables} deliverables
                        </Badge>
                        {stats.subPhasesCount > 0 && (
                          <Badge variant="outline" className="text-[10px] border-cyan-500/50 text-cyan-500">
                            {stats.subPhasesCount} sub-phases
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-[10px] ${stats.completedTasks > 0 ? 'border-green-500/50 text-green-600' : ''}`}>
                          {stats.completedTasks}/{stats.totalTasks} tasks
                        </Badge>
                        
                        {late && <Badge variant="destructive" className="text-[9px]">Late</Badge>}
                        
                        <span className="text-xs font-bold">{stats.percentComplete}%</span>
                      </div>
                      <Progress value={stats.percentComplete} className="h-1.5 mt-2" />
                    </div>
                    
                    {/* Expanded Content */}
                    {!isCollapsed && (
                      <div className="border-t border-white/10 p-3 space-y-3 bg-black/10">
                        {/* Timeline Notes (editable) */}
                        {isAdmin && (
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Timeline Notes</Label>
                            <Input 
                              value={content.phase_data?.[phase.id]?.notes || ''} 
                              onChange={(e) => updatePhaseData(phase.id, 'notes', e.target.value)} 
                              className="h-7 text-xs mt-1" 
                              placeholder="Add timeline notes..."
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                        
                        {/* Phase Deliverables */}
                        {phaseDeliverables.length > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <Label className="text-[10px] text-muted-foreground">Deliverables</Label>
                              <span className="text-[9px] text-muted-foreground">Due → Projected</span>
                            </div>
                            {phaseDeliverables.map((deliverable, idx) => {
                              const deliverableId = deliverable.id || `phase_${phase.id}_del_${idx}`;
                              const projectedDate = getProjectedDate(deliverableId);
                              
                              return (
                                <div 
                                  key={deliverableId} 
                                  className={`p-2 rounded text-xs ${
                                    deliverable.status === 'completed' ? 'bg-green-500/10' : 
                                    isDeliverableLate(deliverable) ? 'bg-red-500/10' : 'bg-white/5'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusOptions.find(s => s.value === deliverable.status)?.color || 'bg-slate-400'}`} />
                                    <span className={`flex-1 min-w-0 truncate ${deliverable.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                      {deliverable.name}
                                    </span>
                                    <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${(deliverable.tasks?.filter(t => t.completed).length || 0) > 0 ? 'border-green-500/50 text-green-500' : ''}`}>
                                      {deliverable.tasks?.filter(t => t.completed).length || 0}/{deliverable.tasks?.length || 0}
                                    </Badge>
                                    <Badge variant="outline" className="text-[9px] flex-shrink-0">
                                      {statusOptions.find(s => s.value === deliverable.status)?.label || 'Planning'}
                                    </Badge>
                                  </div>
                                  {/* Due Date and Projected Completion Date row */}
                                  <div className="flex items-center gap-3 mt-1.5 pl-4">
                                    {/* Due Date (from Scope - read only) */}
                                    <div className="flex items-center gap-1">
                                      <Calendar className={`h-3 w-3 ${isDeliverableLate(deliverable) ? 'text-red-500' : 'text-muted-foreground'}`} />
                                      <span className="text-[10px] text-muted-foreground">Due:</span>
                                      <span className={`text-[10px] ${isDeliverableLate(deliverable) ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                                        {deliverable.due_date ? new Date(deliverable.due_date).toLocaleDateString() : 'Not set'}
                                      </span>
                                    </div>
                                    <span className="text-muted-foreground">→</span>
                                    {/* Projected Completion Date (Timeline specific - editable) */}
                                    <div className="flex items-center gap-1">
                                      <Clock className={`h-3 w-3 ${projectedDate && new Date(projectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`} />
                                      <span className="text-[10px] text-muted-foreground">Projected:</span>
                                      {isAdmin ? (
                                        <Input 
                                          type="date" 
                                          value={projectedDate} 
                                          onChange={(e) => updateProjectedDate(deliverableId, e.target.value)}
                                          className={`h-5 text-[10px] w-[100px] ${projectedDate && deliverable.due_date && new Date(projectedDate) > new Date(deliverable.due_date) ? 'border-amber-500/50 text-amber-500' : ''}`}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      ) : (
                                        <span className={`text-[10px] ${projectedDate && deliverable.due_date && new Date(projectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`}>
                                          {projectedDate ? new Date(projectedDate).toLocaleDateString() : 'Not set'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Sub-Phases */}
                        {subPhases.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-[10px] text-muted-foreground">Sub-Phases</Label>
                            {subPhases.map((subPhase) => {
                              const spDeliverables = subPhase.deliverables || [];
                              const spTasksTotal = spDeliverables.reduce((acc, d) => acc + (d.tasks || []).length, 0);
                              const spTasksCompleted = spDeliverables.reduce((acc, d) => acc + (d.tasks || []).filter(t => t.completed).length, 0);
                              
                              return (
                                <div key={subPhase.id} className="p-2 rounded bg-cyan-500/10 border border-cyan-500/30">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className={`w-2 h-2 rounded-full ${statusOptions.find(s => s.value === subPhase.status)?.color || 'bg-slate-400'}`} />
                                    <span className="text-xs font-medium text-cyan-300 flex-1">{subPhase.name}</span>
                                    {subPhase.due_date && (
                                      <span className="text-[10px] text-muted-foreground">
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                        {new Date(subPhase.due_date).toLocaleDateString()}
                                      </span>
                                    )}
                                    <Badge variant="outline" className="text-[9px]">{spDeliverables.length} del.</Badge>
                                    <Badge variant="outline" className={`text-[9px] ${spTasksCompleted > 0 ? 'border-green-500/50 text-green-500' : ''}`}>
                                      {spTasksCompleted}/{spTasksTotal} tasks
                                    </Badge>
                                  </div>
                                  
                                  {/* Sub-phase deliverables */}
                                  {spDeliverables.length > 0 && (
                                    <div className="mt-2 pl-4 space-y-1">
                                      {spDeliverables.map((deliverable, dIdx) => {
                                        const spDeliverableId = deliverable.id || `sp_${subPhase.id}_del_${dIdx}`;
                                        const spProjectedDate = getProjectedDate(spDeliverableId);
                                        
                                        return (
                                          <div 
                                            key={spDeliverableId} 
                                            className={`p-1.5 rounded text-[11px] ${
                                              deliverable.status === 'completed' ? 'bg-green-500/10' : 
                                              isDeliverableLate(deliverable) ? 'bg-red-500/10' : 'bg-white/5'
                                            }`}
                                          >
                                            <div className="flex items-center gap-2">
                                              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusOptions.find(s => s.value === deliverable.status)?.color || 'bg-slate-400'}`} />
                                              <span className={`flex-1 min-w-0 truncate ${deliverable.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                                {deliverable.name}
                                              </span>
                                              <span className="text-[9px] text-muted-foreground flex-shrink-0">
                                                {deliverable.tasks?.filter(t => t.completed).length || 0}/{deliverable.tasks?.length || 0}
                                              </span>
                                            </div>
                                            {/* Due and Projected dates for sub-phase deliverables */}
                                            <div className="flex items-center gap-2 mt-1 pl-3 text-[9px]">
                                              <span className={`${isDeliverableLate(deliverable) ? 'text-red-500' : 'text-muted-foreground'}`}>
                                                Due: {deliverable.due_date ? new Date(deliverable.due_date).toLocaleDateString() : '-'}
                                              </span>
                                              <span className="text-muted-foreground">→</span>
                                              {isAdmin ? (
                                                <div className="flex items-center gap-1">
                                                  <Clock className={`h-2.5 w-2.5 ${spProjectedDate && deliverable.due_date && new Date(spProjectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`} />
                                                  <Input 
                                                    type="date" 
                                                    value={spProjectedDate} 
                                                    onChange={(e) => updateProjectedDate(spDeliverableId, e.target.value)}
                                                    className={`h-4 text-[9px] w-[90px] ${spProjectedDate && deliverable.due_date && new Date(spProjectedDate) > new Date(deliverable.due_date) ? 'border-amber-500/50 text-amber-500' : ''}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                  />
                                                </div>
                                              ) : (
                                                <span className={`${spProjectedDate && deliverable.due_date && new Date(spProjectedDate) > new Date(deliverable.due_date) ? 'text-amber-500' : 'text-blue-400'}`}>
                                                  Proj: {spProjectedDate ? new Date(spProjectedDate).toLocaleDateString() : '-'}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Empty state */}
                        {phaseDeliverables.length === 0 && subPhases.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-2">No deliverables or sub-phases in this phase</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Feedback Section for Leadership */}
          <GovernanceFeedbackSection 
            pillar={pillar} 
            isAdmin={isAdmin} 
            isLeadership={isLeadership} 
            currentUser={currentUser} 
            onSave={onSave} 
          />
        </div>
        {(isAdmin || isLeadership) && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { TimelineDialog };
